import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AiConnector,
  AiConnectorCatalogEntry,
  authorizeAiConnector,
  connectAiConnector,
  createAiConnector,
  deleteAiConnector,
  getAiConnectorCatalog,
  getAiConnectorRedirectUri,
  getAiConnectors,
  updateAiConnector,
} from 'services'
import { Button, Input2, Modal, TextArea } from 'stories/components'
import { formatTime } from 'utils'

/**
 * Connectors are third-party MCP servers the assistant can call. An admin adds one here for the
 * whole company; each project then chooses which of them it uses (project settings), except for
 * connectors marked always-on.
 */
export function AiConnectors() {
  const [isLoading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [connectors, setConnectors] = useState<AiConnector[]>([])
  const [catalog, setCatalog] = useState<AiConnectorCatalogEntry[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [redirectUri, setRedirectUri] = useState('')
  const [draft, setDraft] = useState({
    name: '',
    url: '',
    description: '',
    token: '',
    catalogKey: '',
    oauthClientId: '',
    oauthClientSecret: '',
  })

  /** The catalog entry behind the current draft, when one was picked. */
  const selectedEntry = catalog.find((entry) => entry.key === draft.catalogKey)

  const reload = () =>
    getAiConnectors()
      .then((data) => setConnectors(data || []))
      .catch(() => {})

  useEffect(() => {
    Promise.all([
      reload(),
      getAiConnectorCatalog()
        .then((data) => setCatalog(data || []))
        .catch(() => {}),
      getAiConnectorRedirectUri()
        .then((data) => setRedirectUri(data?.redirectUri || ''))
        .catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [])

  const onAdd = async () => {
    if (!draft.url.trim()) {
      toast('Enter the connector URL.', { type: 'error' })
      return
    }
    setSaving(true)
    try {
      const created = await createAiConnector({
        name: draft.name,
        url: draft.url.trim(),
        description: draft.description,
        token: draft.token || undefined,
        catalogKey: draft.catalogKey || undefined,
        oauthClientId: draft.oauthClientId || undefined,
        oauthClientSecret: draft.oauthClientSecret || undefined,
      })
      setShowAdd(false)
      setDraft({
        name: '',
        url: '',
        description: '',
        token: '',
        catalogKey: '',
        oauthClientId: '',
        oauthClientSecret: '',
      })
      await reload()
      if (created?.status === 'needs_auth') {
        toast('Added. It needs authorization - use Connect to sign in.', { type: 'info' })
      } else if (created?.status === 'connected') {
        toast(`Connected. ${created.toolCount} tool(s) found.`, { type: 'success' })
      }
    } finally {
      setSaving(false)
    }
  }

  /**
   * Authorization happens in a popup: the connector's own sign-in page runs there, and the backend
   * callback closes it. We reload afterwards rather than waiting on the popup, since we cannot see
   * inside another origin.
   */
  const onConnect = async (connector: AiConnector) => {
    setBusyId(connector.id)
    try {
      const result = await authorizeAiConnector(connector.id)
      if (result?.authorizeUrl) {
        window.open(result.authorizeUrl, 'nexys-connector-auth', 'width=600,height=760')
        toast('Finish signing in to the connector in the window that opened, then refresh.', { type: 'info' })
      } else {
        await connectAiConnector(connector.id)
        toast('Connected.', { type: 'success' })
      }
      await reload()
    } finally {
      setBusyId(null)
    }
  }

  const onRefresh = async (connector: AiConnector) => {
    setBusyId(connector.id)
    try {
      const updated = await connectAiConnector(connector.id)
      await reload()
      if (updated?.status === 'connected') toast(`${updated.toolCount} tool(s) available.`, { type: 'success' })
    } finally {
      setBusyId(null)
    }
  }

  const onToggle = async (connector: AiConnector, field: 'isEnabled' | 'isAlwaysOn' | 'allowWrites') => {
    await updateAiConnector(connector.id, { [field]: !connector[field] } as any)
    reload()
  }

  const onDelete = async (connector: AiConnector) => {
    if (!window.confirm(`Remove the ${connector.name} connector? Projects using it will lose its tools.`)) return
    await deleteAiConnector(connector.id)
    reload()
  }

  const statusBadge = (connector: AiConnector) => {
    if (connector.status === 'connected')
      return (
        <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
          <CheckCircleIcon className="h-3.5 w-3.5" /> Connected
        </span>
      )
    if (connector.status === 'needs_auth')
      return (
        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
          <LockClosedIcon className="h-3.5 w-3.5" /> Needs authorization
        </span>
      )
    if (connector.status === 'error')
      return (
        <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">
          <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Error
        </span>
      )
    return <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Not connected</span>
  }

  return (
    <div className="AiConnectors-container relative">
      <LayoutLoading show={isLoading} />

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI Connectors</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Connect the assistant to other systems by adding their MCP server. Connectors added here belong to the
            company; each project chooses which ones it uses, unless a connector is set to be available everywhere.
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <span className="flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            Add Connector
          </span>
        </Button>
      </div>

      {!isLoading && !connectors.length && (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-gray-500">
          <p className="font-semibold text-gray-700">No connectors yet</p>
          <p className="mt-1 text-sm">Add one to give the assistant access to another system.</p>
        </div>
      )}

      <div className="space-y-3">
        {connectors.map((connector) => (
          <div key={connector.id} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-gray-900">{connector.name}</p>
                  {statusBadge(connector)}
                  {connector.authType === 'oauth' && (
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">OAuth</span>
                  )}
                  {connector.authType === 'token' && connector.hasCredential && (
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Token</span>
                  )}
                </div>
                <p className="mt-1 break-all text-xs text-gray-500">{connector.url}</p>
                {!!connector.description && <p className="mt-1 text-sm text-gray-600">{connector.description}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  {connector.toolCount} tool(s)
                  {connector.status === 'connected' && !connector.allowWrites && (
                    <>
                      {' · '}
                      {connector.annotatesReadOnly
                        ? `${connector.readOnlyToolCount} available while read-only`
                        : 'this server does not mark which tools are read-only, so none are available until writes are allowed'}
                    </>
                  )}
                  {connector.lastConnectedAt ? ` · last checked ${formatTime(connector.lastConnectedAt)}` : ''}
                </p>
                {!!connector.lastError && <p className="mt-1 text-xs text-red-600">{connector.lastError}</p>}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {connector.status !== 'connected' && (
                  <Button size="sm" onClick={() => onConnect(connector)} loading={busyId === connector.id}>
                    Connect
                  </Button>
                )}
                <Button size="sm" color="gray" onClick={() => onRefresh(connector)} loading={busyId === connector.id}>
                  <span className="flex items-center gap-1.5">
                    <ArrowPathIcon className="h-4 w-4" />
                    Refresh
                  </span>
                </Button>
                <button
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Remove"
                  onClick={() => onDelete(connector)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 border-t border-gray-100 pt-3 text-sm">
              <label className="flex items-center gap-2 text-gray-700">
                <input type="checkbox" checked={connector.isEnabled} onChange={() => onToggle(connector, 'isEnabled')} />
                Enabled
              </label>
              <label className="flex items-center gap-2 text-gray-700">
                <input
                  type="checkbox"
                  checked={connector.isAlwaysOn}
                  onChange={() => onToggle(connector, 'isAlwaysOn')}
                />
                Available in the AI Assistant and every project
              </label>
              <label className="flex items-center gap-2 text-gray-700">
                <input
                  type="checkbox"
                  checked={connector.allowWrites}
                  onChange={() => onToggle(connector, 'allowWrites')}
                />
                Allow this connector to make changes
              </label>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <Modal
          title="Add Connector"
          titleOkay="Add"
          isOpen
          loading={saving}
          onOk={onAdd}
          onClose={() => setShowAdd(false)}
        >
          <div className="w-[34rem] max-w-full">
            {!!catalog.length && (
              <div className="mb-4">
                <p className="mb-1.5 text-sm font-medium text-gray-700">Start from a known connector</p>
                <div className="flex flex-wrap gap-2">
                  {catalog.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      onClick={() =>
                        setDraft({
                          name: entry.name,
                          url: entry.url,
                          description: entry.description,
                          token: '',
                          catalogKey: entry.key,
                          oauthClientId: '',
                          oauthClientSecret: '',
                        })
                      }
                      className={`rounded-md border px-2.5 py-1 text-sm ${
                        draft.catalogKey === entry.key
                          ? 'border-shade-blue bg-shade-blue/5 text-shade-blue'
                          : 'border-gray-300 text-gray-700 hover:border-shade-blue'
                      }`}
                    >
                      {entry.name}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-gray-500">Or paste any MCP server URL below.</p>
              </div>
            )}

            <Input2
              title="Connector URL"
              required
              value={draft.url}
              onChange={(value: string) => setDraft({ ...draft, url: value, catalogKey: '' })}
            />
            <div className="mt-4">
              <Input2 title="Name" value={draft.name} onChange={(value: string) => setDraft({ ...draft, name: value })} />
            </div>
            <div className="mt-4">
              <TextArea
                title="Description"
                rows={2}
                value={draft.description}
                onChange={(value: string) => setDraft({ ...draft, description: value })}
              />
            </div>
            {(selectedEntry?.needsClientId || !!draft.oauthClientId) && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">
                  {selectedEntry?.name || 'This connector'} needs an OAuth client
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  It does not register applications automatically. Create an OAuth app in{' '}
                  {selectedEntry?.name || 'the connector'} using this redirect URL, then paste its client id below.
                </p>
                {!!redirectUri && (
                  <code className="mt-1.5 block break-all rounded bg-white px-2 py-1 text-xs text-gray-800">
                    {redirectUri}
                  </code>
                )}
                <div className="mt-3">
                  <Input2
                    title="Client ID"
                    value={draft.oauthClientId}
                    onChange={(value: string) => setDraft({ ...draft, oauthClientId: value })}
                  />
                </div>
                <div className="mt-3">
                  <Input2
                    title="Client secret (if the vendor issues one)"
                    value={draft.oauthClientSecret}
                    onChange={(value: string) => setDraft({ ...draft, oauthClientSecret: value })}
                  />
                </div>
              </div>
            )}

            <div className="mt-4">
              <Input2
                title="API key (only if the connector uses one)"
                value={draft.token}
                onChange={(value: string) => setDraft({ ...draft, token: value })}
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave this empty for connectors that sign in. If the server asks for authorization, you will be
                prompted to connect after adding it. Keys are stored encrypted and never shown again.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
